import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { LoanAndCreditPaymentAccountType } from '/@/modules/liability/enums/LoanAndCreditPaymentAccountType';
import { FinancialAccountSubType } from '/@/modules/financialaccount/constants/FinancialAccountSubType';
import Lang from '/@/modules/core/app/services/Lang';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { FinancialAccountType } from '/@/modules/financialaccount/constants/FiancialAccountType';
import { FinancialAccountMetaType } from '/@/modules/financialaccount/constants/FinancialAccountMetaType';
import { FinancialAccountMeta } from '/@/modules/financialaccount/models/meta/FinancialAccountMeta';
import { FinancialAccountRrifType } from '/@/modules/financialaccount/constants/FinancialAccountRrifType';
import { FinancialAccountTfsaType } from '/@/modules/financialaccount/constants/FinancialAccountTfsaType';
import { FinancialAccountSyncStatus } from '/@/modules/financialgateway/constants/FinancialAccountSyncStatus';
import { maskLastDigits } from '@/modules/core/app/helpers/StringHelper';
import { isToday } from '@/modules/core/app/helpers/DateHelper';
import { FinancialInstitution } from '@/modules/predefined/models/FinancialInstitution';
import { Asset } from '@/modules/asset/models/Asset';
import { Liability } from '@/modules/liability/models/Liability';
import { FinancialGatewayToken } from '@/modules/financialgateway/models/FinancialGatewayToken';
import { FinancialAccountTransaction } from '@/modules/financialaccount/models/FinancialAccountTransaction';
import { EstateBankingAccount } from '@/modules/estatebanking/models/EstateBankingAccount';
import { StockHolding } from '/@/modules/stock/models/StockHolding';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { Document } from '@/modules/document/models/Document';
import { FinancialAccountMetaFactory } from '@/modules/financialaccount/models/meta/FinancialAccountMetaFactory';
import dayjs from 'dayjs';

export class FinancialAccount extends BaseEntityModel {
  id?: string;

  plaidAccountId?: string;

  flinksAccountId?: string;

  name?: string;

  number?: string;

  mask?: string;

  metaType?: string;

  institutionNumber?: string;

  transitNumber?: string;

  subType?: string;

  type?: string;

  financialInstitutionId?: number;

  estateId?: number;

  estateAccountId?: number;

  financialGatewayTokenId?: number;

  isEstateBankAccount?: boolean;

  isManual?: boolean;

  syncStatus?: string;

  lastSyncedAt?: string;

  isDepository?: boolean;

  isRegisteredInvestment?: boolean;

  isNonRegisteredInvestment?: boolean;

  financialInstitution: FinancialInstitution | null;

  asset: Asset | null;

  liability: Liability | null;

  financialGatewayToken: FinancialGatewayToken | null;

  financialAccountTransactions: FinancialAccountTransaction[] | null;

  estateAccount: EstateAccount | null;

  balance?: number;

  lastUpdateAt?: string;

  estateBankingAccount: EstateBankingAccount | null;

  meta: FinancialAccountMeta | null;

  stockHoldings: StockHolding[] | null;

  documents: Document[] | null;

  bankStatements: Document[] | null;

  constructor(model: Partial<FinancialAccount> = {}) {
    super(model);

    this.id = model.id;
    this.name = model.name;
    this.number = model.number;
    this.mask = model.mask;
    this.metaType = model.metaType;
    this.institutionNumber = model.institutionNumber;
    this.lastUpdateAt = model.lastUpdateAt;
    this.balance = model.balance;
    this.transitNumber = model.transitNumber;
    this.subType = model.subType;
    this.type = model.type;
    this.syncStatus = model.syncStatus;
    this.lastSyncedAt = model.lastSyncedAt;
    this.financialInstitutionId = model.financialInstitutionId;
    this.estateId = model.estateId;
    this.estateAccountId = model.estateAccountId;
    this.financialGatewayTokenId = model.financialGatewayTokenId;
    this.plaidAccountId = model.plaidAccountId;
    this.flinksAccountId = model.flinksAccountId;
    this.isNonRegisteredInvestment = model.isNonRegisteredInvestment;
    this.isRegisteredInvestment = model.isRegisteredInvestment;
    this.isEstateBankAccount = model.isEstateBankAccount;
    this.isManual = model.isManual;
    this.isDepository = model.isDepository;

    this.financialInstitution = FinancialInstitution.mapModelOrNull(model.financialInstitution);
    this.asset = Asset.mapModelOrNull(model.asset);
    this.liability = Liability.mapModelOrNull(model.liability);
    this.financialGatewayToken = FinancialGatewayToken.mapModelOrNull(model.financialGatewayToken);
    this.financialAccountTransactions = FinancialAccountTransaction.mapModelsOrNull(
      model.financialAccountTransactions
    );
    this.estateAccount = EstateAccount.mapModelOrNull(model.estateAccount);
    this.estateBankingAccount = EstateBankingAccount.mapModelOrNull(model.estateBankingAccount);
    this.stockHoldings = StockHolding.mapModelsOrNull(model.stockHoldings);
    this.meta = FinancialAccountMetaFactory.factory(model);
    this.documents = Document.mapModelsOrNull(model.documents);
    this.bankStatements = Document.mapModelsOrNull(model.bankStatements);
  }

  getModelName(): ModelType {
    return ModelType.FINANCIAL_ACCOUNT;
  }

  get title(): string {
    return `${Lang.t(this.financialInstitution!.name)}, ${Lang.t(this.bankAccountSubType)} ${
      this.number ? '-' : ''
    } ${this.number ?? ''}`;
  }

  get liabilityTitle(): string {
    return `${this.financialInstitution!.name}, ${
      LoanAndCreditPaymentAccountType.getByKey(this.subType)?.value
    } ${this.number ? '-' : ''} ${this.number ?? ''}`;
  }

  get displayValue(): string {
    if (!this.hasDateOfDeathValue) {
      return '';
    }
    if (!this.id) {
      return '-';
    }

    if (!this.asset && !this.liability) {
      return Lang.t('financialaccount.75a50580-96dc-49c6-ac06-ab605c05a835');
    }

    return this.asset
      ? NumeralService.formatValue(this.asset.currentValue, ColumnFormat.FORMAT_CURRENCY)
      : this.liabilityDisplayValue;
  }

  get formattedValue(): string {
    if (!this.asset && !this.liability) {
      return Lang.t('financialaccount.75a50580-96dc-49c6-ac06-ab605c05a835');
    }

    return this.asset
      ? NumeralService.formatValue(this.asset.currentValue, ColumnFormat.FORMAT_CURRENCY, 2)
      : this.liabilityDisplayValue;
  }

  get estateBankingAccountBalance(): string {
    return NumeralService.formatValue(this.balance, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  get displayName(): string {
    return this.asset ? Lang.t('accounting.asset') : Lang.t('accounting.liability');
  }

  get hasSyncStatus(): boolean {
    return !!this.syncStatus;
  }

  get isFlinksAccount(): boolean {
    return !!this.flinksAccountId || !!this.syncStatus;
  }

  get isFlinksPending(): boolean {
    return this.syncStatus === FinancialAccountSyncStatus.PENDING;
  }

  get isRefreshingBalances(): boolean {
    return this.syncStatus === FinancialAccountSyncStatus.REFRESHING_BALANCE;
  }

  get hasSyncedToday(): boolean {
    if (!this.lastSyncedAt) {
      return false;
    }

    return isToday(this.lastSyncedAt);
  }

  get isMortgage(): boolean {
    return (
      this.subType === LoanAndCreditPaymentAccountType.MORTGAGE.key ||
      this.subType === LoanAndCreditPaymentAccountType.HOME_EQUITY.key
    );
  }

  get isConstructionLoan(): boolean {
    return this.subType === LoanAndCreditPaymentAccountType.CONSTRUCTION_LOAN.key;
  }

  get canBeLinkedWithAsset(): boolean {
    return (
      [
        LoanAndCreditPaymentAccountType.OTHER.key,
        LoanAndCreditPaymentAccountType.LINE_OF_CREDIT.key,
        LoanAndCreditPaymentAccountType.CONSUMER_LOAN.key
      ].includes(this.subType ?? '') || this.requiresLinkWithAsset
    );
  }

  get requiresLinkWithAsset(): boolean {
    return [
      LoanAndCreditPaymentAccountType.AUTO.key,
      LoanAndCreditPaymentAccountType.MORTGAGE.key,
      LoanAndCreditPaymentAccountType.HOME_EQUITY.key,
      LoanAndCreditPaymentAccountType.CONSTRUCTION_LOAN.key,
      LoanAndCreditPaymentAccountType.COMMERCIAL_LOAN.key,
      LoanAndCreditPaymentAccountType.LOAN_RRSP.key
    ].includes(this.subType ?? '');
  }

  get isAutoLoan(): boolean {
    return this.subType === LoanAndCreditPaymentAccountType.AUTO.key;
  }

  get lastFourNumberDigits(): string | undefined {
    return this.number?.substr(this.number.length - 4);
  }

  get isTaxApplicableSubType(): boolean {
    return (
      !!this.subType && FinancialAccountSubType.getIsTaxApplicableSubType().includes(this.subType)
    );
  }

  get isRegisteredInvestmentSubType(): boolean {
    return (
      !!this.subType &&
      FinancialAccountSubType.getRegisteredInvestmentSubTypes().includes(this.subType)
    );
  }

  get isInvestmentType(): boolean {
    return this.type === FinancialAccountType.INVESTMENT;
  }

  get isExpired(): boolean {
    return !!this.financialGatewayToken?.expired;
  }

  get liabilityDisplayValue(): string {
    const value: string = NumeralService.formatValue(
      this.liability!.displayAmount,
      ColumnFormat.FORMAT_CURRENCY
    );

    return this.liability!.displayAmount === 0 ? value : `-${value}`;
  }

  get hasDateOfDeathValue(): boolean {
    return this.asset ? !!this.asset?.atDeathValue : !!this.liability?.atDeathAmount;
  }

  get dateOfDeathDisplayValue(): string {
    if (!this.asset && !this.liability) {
      return '';
    }

    return this.asset
      ? NumeralService.formatValue(this.asset!.atDeathValue, ColumnFormat.FORMAT_CURRENCY)
      : this.liabilityAtDeathValueDisplayValue;
  }

  get liabilityAtDeathValueDisplayValue(): string {
    const value: string = NumeralService.formatValue(
      this.liability!.atDeathAmount,
      ColumnFormat.FORMAT_CURRENCY
    );
    return this.liability!.atDeathAmount <= 0 ? value : `-${value}`;
  }

  get assetUpdatedAtFromNow(): string {
    return this.asset?.updatedAtFromNow ?? '-';
  }

  get estateBankAccountName(): string {
    return this.financialInstitution?.name ?? '-';
  }

  get bankAccountSubType(): string {
    return FinancialAccountSubType.getByKey(this.subType)?.value ?? '';
  }

  get accountNumberLastFourDigits(): string {
    return this.number && this.number.length > 4
      ? this.number!.substring(this.number!.length - 4)
      : '';
  }

  get maskedAccountNumberLastFourDigits(): string {
    return maskLastDigits(this.number!, 4);
  }

  get hasTransitAndAccountNumber(): boolean {
    return !!this.transitNumber && !!this.number;
  }

  get isTFSA(): boolean {
    return this.subType === FinancialAccountSubType.INVESTMENT_TFSA.key;
  }

  get isRRIF(): boolean {
    return this.subType === FinancialAccountSubType.INVESTMENT_RRIF.key;
  }

  get isAnnuitantTypeMeta(): boolean {
    return this.meta?.type === FinancialAccountRrifType.ANNUITANT.key;
  }

  get isSuccessorHolderTypeMeta(): boolean {
    return this.meta?.type === FinancialAccountTfsaType.SUCCESSOR_HOLDER.key;
  }

  get isNamedBeneficiariesTypeMeta(): boolean {
    return this.meta?.type === FinancialAccountRrifType.NAMED_BENEFICIARIES.key;
  }

  get isNoneTypeMeta(): boolean {
    return this.meta?.type === FinancialAccountRrifType.NONE.key;
  }

  get isComplete(): boolean {
    return !!this.id;
  }

  get isSegregatedFunds(): boolean {
    return this.subType === FinancialAccountSubType.INVESTMENT_SEGREGATED_FUNDS.key;
  }

  setMetaType(subType: FinancialAccountSubType): void {
    if (subType.key === FinancialAccountSubType.INVESTMENT_RRIF.key) {
      this.metaType = FinancialAccountMetaType.FINANCIAL_ACCOUNT_RRIF_META;
      this.meta = FinancialAccountMetaFactory.factory(this);
    }
    if (subType.key === FinancialAccountSubType.INVESTMENT_TFSA.key) {
      this.metaType = FinancialAccountMetaType.FINANCIAL_ACCOUNT_TFSA_META;
      this.meta = FinancialAccountMetaFactory.factory(this);
    }
  }

  get truncatedAccountNumber(): string {
    return `(...${this.number!.substring(this.number!.length - 4)})`;
  }

  get subTypeCanHaveStockHolding(): boolean {
    return (
      !!this.subType && FinancialAccountSubType.getSubTypesWithStockHolding().includes(this.subType)
    );
  }

  setFinancialInstitution(value: FinancialInstitution): void {
    this.type = FinancialAccountType.DEPOSITORY;
    this.subType = FinancialAccountSubType.DEPOSITORY_CHECKING.key;
    this.financialInstitutionId = value.id ? parseInt(value.id) : undefined;
    this.financialInstitution = value;
  }

  updateTypeBasedOnSubType(subType: FinancialAccountSubType): void {
    this.type = FinancialAccountSubType.getParentKeyFromSubKey(subType);
  }

  get formattedLastSyncedAt(): string {
    return this.lastSyncedAt ? dayjs(this.lastSyncedAt).fromNow() : '-';
  }

  get bankStatementWarning(): string {
    if (!this.bankStatements?.length) {
      return '';
    }

    const diff: number = dayjs().diff(dayjs(this.bankStatements.at(-1)!.createdAt), 'day');

    if (diff > 30) {
      return Lang.t('financialaccount.no-uploaded-last', { diff });
    }

    return '';
  }

  get accountHeaderName(): string {
    return Lang.t('financialaccount.estate-subtype-account', {
      subType: this.bankAccountSubType,
      digits: this.accountNumberLastFourDigits
    });
  }

  updateTransactions(transactions: FinancialAccountTransaction[]): void {
    transactions.forEach((updatedTransaction: FinancialAccountTransaction) => {
      const currentTransaction: FinancialAccountTransaction | undefined =
        this.financialAccountTransactions?.find(
          (transaction: FinancialAccountTransaction) => transaction.id === updatedTransaction.id
        );

      if (!currentTransaction) {
        return;
      }

      currentTransaction.updateFromTransaction(updatedTransaction);
      currentTransaction.setOriginal();
    });
  }

  getCurrentlyLinkedTransactions(
    modelType: string,
    modelId: number
  ): FinancialAccountTransaction[] {
    return (
      this.financialAccountTransactions?.filter((transaction: FinancialAccountTransaction) =>
        transaction.isLinkedToEntity(modelType, modelId)
      ) ?? []
    );
  }

  getLinkableTransactions(modelType: string, modelId: number): FinancialAccountTransaction[] {
    return (
      this.financialAccountTransactions?.filter(
        (transaction: FinancialAccountTransaction) =>
          transaction.isLinkedToEntity(modelType, modelId) ||
          (!transaction.category && transaction.canLinkToEntity(modelType))
      ) ?? []
    );
  }
}
